import React from "react";
import ReactDOM from "react-dom";
import TableHeader from "./TableHeader.js";
import TableRow from "./TableRow.js";
import { SortDirection, Table as InnerTable, Column, AutoSizer, InfiniteLoader } from "react-virtualized";
import _ from "underscore";
import TableColumnChooser from "./TableColumnChooser.js";
const ROW_HEIGHT = 20;
const ROW_HEIGHT_TOUCH = 25;
const MAX_COLUMNS = 15;
const COLUMN_FRACTION_BASE = 10000;
const MIN_COLUMN_PX_WIDTH = 30;
const HIGHLIGHT = {
  NONE: "none",
  SINGLE: "single",
  MULTI: "multi"
};
class Table extends React.Component {
  state = {
    columnWidths: [],
    dragData: null,
    selectedIndexes: []
  };
  componentDidMount() {
    if (!this.props.columnWidths && this.props.columns || this.props.columnWidths.length !== this.props.columns.length) {
      this.setState({
        columnWidths: this.getDefaultColumnWidths(this.props.columns)
      });
    }
    if (this.props.setClearSelection) {
      this.props.setClearSelection(this.clearSelection);
    }
    if (this.props.registerSelectAll) {
      this.props.registerSelectAll(this.selectAll);
    }
    if (this.props.registerSetSelected) {
      this.props.registerSetSelected(this.setSelected);
    }
    if (this.props.registerReloadVisibleRows) {
      this.props.registerReloadVisibleRows(this.reloadVisibleRows);
    }
  }
  componentDidUpdate(prevProps) {
    let widthsSet = false;
    if (this.props.columnWidths && !_.isEqual(this.props.columnWidths, []) && !_.isEqual(this.props.columnWidths, this.state.columnWidths)) {
      if (this.props.columnWidths.length === this.props.columns.length) {
        const newWidths = this.floatsToFraction(this.props.columnWidths, COLUMN_FRACTION_BASE);
        if (!_.isEqual(newWidths, this.state.columnWidths)) {
          widthsSet = true;
          this.setState({
            columnWidths: newWidths
          });
        }
      }
    }
    if (!this.props.columnWidths && this.props.columns) {
      const newWidths = this.getDefaultColumnWidths(this.props.columns);
      if (!_.isEqual(newWidths, this.state.columnWidths)) {
        widthsSet = true;
        this.setState({
          columnWidths: newWidths
        });
      }
    }
    if (this.props.updatedIds !== prevProps.updatedIds) {
      this.updateLabels(this.props.defaultColumns, true);
      if (this.state.selectedIndexes.length > 0) {
        this.setState({
          selectedIndexes: []
        });
      }
    }
    if ((!this.state.columnWidths || this.state.columnWidths.length !== this.props.columns.length) && !widthsSet && this.props.columns.length > 0) {
      this.setState({
        columnWidths: this.getDefaultColumnWidths(this.props.columns)
      });
    }
    if (_.isString(this.props.width)) {
      const node = ReactDOM.findDOMNode(this);
      if (node && node.clientWidth !== this.state.pixelWidth) {
        this.setState({
          pixelWidth: node.clientWidth
        });
      }
    }
  }
  getDefaultColumnWidths = columns => {
    const pxWidth = this.getPixelWidth();
    const numDynamicWeight = columns.filter(column => column.defaultWidth === undefined).length;
    const totalSetWidth = columns.reduce((result, column) => {
      if (column.defaultWidth) {
        return result + column.defaultWidth;
      }
      return result;
    }, 0);
    const defaultWidth = (pxWidth - totalSetWidth) / numDynamicWeight / pxWidth;
    const result = columns.map(column => {
      if (column.defaultWidth !== undefined) {
        return column.defaultWidth / pxWidth;
      }
      return defaultWidth;
    });
    return this.floatsToFraction(result, COLUMN_FRACTION_BASE);
  };
  floatsToFraction = (floats, base) => {
    const fractions = floats.map(float => Math.round(float * base)).map(fraction => Math.max(1, Math.min(fraction, base)));
    const sum = fractions.reduce((total, fraction) => fraction + total, 0);
    if (sum === base || floats.length === 0) {
      return fractions;
    }
    if (sum > base) {
      const diffPerFraction = Math.floor((sum - base) / fractions.length);
      const remainder = (sum - base) % fractions.length;
      return fractions.map((fraction, index) => {
        const newFraction = fraction - diffPerFraction;
        if (index >= fractions.length - remainder) {
          return newFraction - 1;
        }
        return newFraction;
      });
    }

    // sum < base
    const diffPerFraction = Math.ceil((sum - base) / fractions.length);
    const remainder = (base - sum) % fractions.length;
    return fractions.map((fraction, index) => {
      const newFraction = fraction - diffPerFraction;
      if (index >= fractions.length - remainder) {
        return newFraction + 1;
      }
      return newFraction;
    });
  };
  fractionsToFloat = (fractions, base) => {
    return fractions.map(fraction => fraction / base);
  };

  // If a resize drag is detected, return the columns between which the drag is being performed
  getColumnResizeData = (xPosition, event) => {
    const relativePosition = xPosition / this.getPixelWidth();
    const mousePosition = Math.max(0, Math.min(COLUMN_FRACTION_BASE, Math.round(COLUMN_FRACTION_BASE * relativePosition)));
    if (event.target.className.indexOf("resizeArea") === 0) {
      const dataKey = event.target.getAttribute("data-key");
      const index = this.getColumnIndexOfDataKey(dataKey);
      return {
        first: this.props.columns[index],
        second: this.props.columns[index + 1],
        originalWidth: this.state.columnWidths[index] + this.state.columnWidths[index + 1],
        startPosition: mousePosition
      };
    }
    return false;
  };
  beginColumnResize = event => {
    const offset = _.nodeOffset(this.refs.objectTable);
    const x = _.getClientPos(event).x - offset.left;
    const y = _.getClientPos(event).y - offset.top;
    if (this.getColumnResizeData(x, event) === false || y > ROW_HEIGHT) {
      return;
    }
    document.addEventListener("mousemove", this.resizeColumn);
    document.addEventListener("mouseup", this.endColumnResize);
    event.preventDefault();
  };
  resizeColumn = event => {
    const offset = _.nodeOffset(this.refs.objectTable);
    const x = _.getClientPos(event).x - offset.left;
    const data = this.state.dragData ? this.state.dragData : this.getColumnResizeData(x, event);
    if (!data || typeof data === "string") {
      this.setState({
        dragData: null
      });
      return;
    }
    const relativePosition = Math.round(x / this.getPixelWidth() * COLUMN_FRACTION_BASE);
    const difference = relativePosition - data.startPosition;
    const firstIndex = _.findIndex(this.props.columns, col => col.id === data.first.id);
    const secondIndex = _.findIndex(this.props.columns, col => col.id === data.second.id);
    let firstWidth = this.state.columnWidths[firstIndex] + difference;
    let secondWidth = this.state.columnWidths[secondIndex] - difference;
    const MIN_WIDTH = Math.round(MIN_COLUMN_PX_WIDTH / this.getPixelWidth() * COLUMN_FRACTION_BASE);
    if (firstWidth < MIN_WIDTH) {
      secondWidth = data.originalWidth - MIN_WIDTH;
      firstWidth = MIN_WIDTH;
    } else if (secondWidth < MIN_WIDTH) {
      firstWidth = data.originalWidth - MIN_WIDTH;
      secondWidth = MIN_WIDTH;
    }
    const newColumnWidths = [].concat(this.state.columnWidths);
    newColumnWidths[firstIndex] = firstWidth;
    newColumnWidths[secondIndex] = secondWidth;
    data.startPosition = relativePosition;
    this.setState({
      dragData: data
    });
    this.columnWidthsChange(newColumnWidths);
  };
  endColumnResize = () => {
    document.removeEventListener("mousemove", this.resizeColumn);
    document.removeEventListener("mouseup", this.endColumnResize);
    if (!this.state.dragData) {
      return;
    }

    // Catch any click events to prevent involuntary sorting
    document.addEventListener("click", _.stopPropagation, true);
    const CLICK_TIMEOUT = 30;
    window.setTimeout(() => {
      document.removeEventListener("click", _.stopPropagation, true);
    }, CLICK_TIMEOUT);

    // Clean up after resize
    this.setState({
      dragData: null
    });
  };
  getColumnIndexOfDataKey = dataKey => {
    return _.findIndex(this.props.columns, {
      name: dataKey
    });
  };
  isFirstColumn = dataKey => {
    return this.getColumnIndexOfDataKey(dataKey) === 0;
  };
  isLastColumn = dataKey => {
    return this.getColumnIndexOfDataKey(dataKey) === this.props.columns.length - 1;
  };
  onColumnDragStart = (dataKey, evt) => {
    // dataKey is dataKey of column
    const index = this.getColumnIndexOfDataKey(dataKey);
    _.setDragData(evt.nativeEvent, "application/json", JSON.stringify(index));
    this.setState({
      dragData: JSON.stringify(index)
    });
  };
  onColumnDrop = (dataKey, evt) => {
    // dataKey, not index like it was in our own Table
    const index = this.getColumnIndexOfDataKey(dataKey);
    const data = _.getDragData(evt.nativeEvent, "application/json");
    if (!data || data === "") {
      return;
    }
    const currentIndex = JSON.parse(data);
    if (currentIndex === -1) {
      // We dropped something which is not in the column list
      return;
    }
    evt.stopPropagation();
    if (index === currentIndex) {
      return;
    }
    this.moveColumnToIndex(currentIndex, index);
  };
  moveColumnToIndex = (from, to) => {
    const columns = [].concat(this.props.columns);
    const col = columns.splice(from, 1)[0];
    columns.splice(to, 0, col);
    const widths = [].concat(this.state.columnWidths);
    const width = widths.splice(from, 1)[0];
    widths.splice(to, 0, width);
    this.columnWidthsChange(widths);
    this.onColumnChange(columns);
  };
  onSelect = (index, rowData, event) => {
    if (this.props.highlight === HIGHLIGHT.NONE) {
      this.props.onSelect(index, [index], event);
      return;
    }
    const newIndexes = this.setSelected(index, event);
    this.props.onSelect(index, newIndexes, event);
  };
  allowsMultiSelection = () => {
    return this.props.highlight === HIGHLIGHT.MULTI;
  };
  setSelected = (index, event) => {
    let newSelection = [];
    if (index === false) {
      this.setState({
        selectedIndexes: newSelection
      });
      return newSelection;
    }
    if (this.allowsMultiSelection() && event && _.isModKey(event)) {
      if (this.isSelected(index)) {
        newSelection = this.state.selectedIndexes.filter(selectedIndex => selectedIndex !== index);
      } else {
        newSelection = this.state.selectedIndexes.concat(index);
      }
    } else if (this.allowsMultiSelection() && event && event.shiftKey) {
      const latestIndex = this.state.selectedIndexes[this.state.selectedIndexes.length - 1] || 0;
      const newSelectedIndexes = _.range(Math.min(index, latestIndex), Math.max(index, latestIndex) + 1);
      newSelection = _.union(this.state.selectedIndexes, newSelectedIndexes);
    } else {
      // Simple click, make selection the clicked row.
      newSelection = [index];
    }
    if (index >= this.state.firstIndex && index < this.getLastVisibleIndex() - 1) {
      this.setState({
        selectedIndexes: newSelection
      });
      return newSelection;
    }
    this.setState({
      selectedIndexes: newSelection
    });
    return newSelection;
  };
  isSelected = index => {
    if (!this.state.selectedIndexes) {
      return false;
    }
    return this.state.selectedIndexes.indexOf(index) !== -1;
  };
  isHover = index => {
    if (_.isNullish(this.props.hoverIndex)) {
      return false;
    }
    return this.props.hoverIndex === index;
  };
  showColumnChooser = () => {
    const primaryColumns = this.props.allColumns.filter(column => column.primary === true);
    const primaryIds = primaryColumns.map(column => column.id);
    const sortedPrimary = _.sortBy(primaryColumns, "name");
    const otherColumns = this.props.allColumns.filter(column => column.primary !== true);
    let sortedColumns = _.sortBy(otherColumns, "name");
    sortedColumns = sortedPrimary.concat(sortedColumns);
    let defaultSelected = sortedColumns.filter(column => _.pluck(this.props.columns, "id").indexOf(column.id) !== -1);
    defaultSelected = _.pluck(defaultSelected, "id");
    const updateColumns = (columnId, remove) => {
      if (remove) {
        this.removeColumn(parseInt(columnId, 10));
      } else {
        this.addColumn(_.findWhere(sortedColumns, {
          id: parseInt(columnId, 10)
        }));
      }
    };
    const displayKey = this.props.modalKey || undefined;
    const modal = /*#__PURE__*/React.createElement(TableColumnChooser, {
      logInfo: this.props.log.info,
      language: this.props.language,
      selectedColumns: defaultSelected,
      updateColumns: updateColumns,
      allColumns: sortedColumns,
      maxColumns: this.props.maxColumns || MAX_COLUMNS,
      primaryIds: primaryIds
    });
    this.props.presentModal(/*#__PURE__*/React.createElement("div", null, modal), displayKey, this.props.language.get("dynamic_reserv_list_columns"));
  };
  removeColumn = columnId => {
    const columns = [].concat(this.props.columns);
    const widths = [].concat(this.state.columnWidths);
    const index = _.findIndex(columns, column => column.id === columnId);
    const removedWidth = this.state.columnWidths[index];
    if (index + 1 === widths.length) {
      widths[index - 1] = widths[index - 1] + removedWidth;
    } else {
      widths[index + 1] = widths[index + 1] + removedWidth;
    }
    widths.splice(index, 1);
    columns.splice(index, 1);
    this.onColumnChange(columns);
    this.columnWidthsChange(widths);
  };
  columnWidthsChange = newWidths => {
    this.setState({
      columnWidths: newWidths
    });
    if (this.props.onColumnWidthChange) {
      this.props.onColumnWidthChange(this.fractionsToFloat(newWidths, COLUMN_FRACTION_BASE));
    }
  };
  onColumnChange = newColumns => {
    if (this.props.onColumnChange) {
      this.props.onColumnChange(newColumns);
    }
  };
  addColumn = column => {
    const newColumns = this.props.columns.concat(column);
    const widths = [].concat(this.state.columnWidths);
    const MIN_WIDTH = COLUMN_FRACTION_BASE / MAX_COLUMNS;
    let requiredWidth = Math.max(widths[widths.length - 1] / 2, MIN_WIDTH);
    const newWidth = requiredWidth;

    // Take the required space from the closest columns without allowing
    // any column to be smaller than the minimum width.
    let index = widths.length - 1;
    while (requiredWidth > 0 && index >= 0) {
      const width = widths[index];
      if (width > MIN_WIDTH) {
        const widthToRemove = Math.min(width - MIN_WIDTH, requiredWidth);
        widths[index] = width - widthToRemove;
        requiredWidth = requiredWidth - widthToRemove;
      }
      index--;
    }
    widths.push(newWidth);
    this.onColumnChange(newColumns);
    this.columnWidthsChange(widths);
    if (this.props.reload) {
      this.props.reload();
    }
  };
  onPlusButtonClick = () => {
    if (this.props.columns.length === 0) {
      return;
    }
    this.showColumnChooser();
  };
  columnWithDataKey = dataKey => {
    return _.find(this.props.columns, col => col.name === dataKey);
  };
  getHeaderMenuItems = dataKey => {
    const column = this.columnWithDataKey(dataKey);
    if (this.props.columns.length === 1) {
      return [];
    }
    const hasSinglePrimaryColumn = this.props.columns.filter(col => col.primary).length === 1;
    if (hasSinglePrimaryColumn && column.primary) {
      return [];
    }
    return [{
      key: `column.remove.${column.id}`,
      label: this.props.language.get("dynamic_object_info_remove"),
      action: this.removeColumn.bind(null, column.id)
    }];
  };
  clearSelection = () => {
    this.setState({
      selectedIndexes: []
    });
  };
  selectAll = () => {
    let selectedIndexes;
    if (this.state.selectedIndexes.length === this.props.rowCount) {
      selectedIndexes = [];
    } else {
      selectedIndexes = _.range(0, this.props.rowCount);
    }
    this.setState({
      selectedIndexes
    });
    this.props.onSelect(0, selectedIndexes);
  };
  getRowMenuItems = (indexes, refreshCallback) => {
    if (!this.props.getRowMenuItems) {
      return [];
    }
    return this.props.getRowMenuItems(indexes, (selectAll = false) => {
      // The invoked function may invoke the second parameter to clear the selection.
      this.clearSelection();
      if (selectAll) {
        this.selectAll();
      }
    }, refreshCallback);
  };
  rowRenderer = props => {
    const isHover = this.isHover(props.index);
    const isSelected = this.isSelected(props.index);
    if (isSelected || isHover) {
      if (props.rowData.color) {
        props.style.border = "1px dotted";
        props.style.filter = "brightness(85%)";
      } else if (isHover && !isSelected) {
        props.style.backgroundColor = "#f7f7f7";
      } else {
        props.style.backgroundColor = "#e3e3e3";
      }
    }
    if (props.rowData.textColor) {
      props.style.color = props.rowData.textColor;
    }
    if (props.rowData.color) {
      props.style.backgroundColor = props.rowData.color;
    }
    if (props.rowData.italicize) {
      props.style.fontStyle = "italic";
    }
    if (props.rowData.bold) {
      props.style.fontWeight = "bold";
    }
    if (props.rowData.textAlign === "right") {
      props.style.textAlign = "right";
    }
    return /*#__PURE__*/React.createElement(TableRow, {
      onDragStart: this.props.onDragStart,
      onSelect: this.onSelect,
      getRowMenuItems: this.getRowMenuItems,
      rowKey: props.key,
      onRowHover: this.props.onRowHover,
      hoverButton: this.props.hoverButton,
      contextMenu: this.props.contextMenu,
      ...props
    });
  };
  getCellRenderer = (column, props) => {
    if (props.cellRenderers && props.cellRenderers[column.id]) {
      return props.cellRenderers[column.id];
    }
    if (props.cellRenderers && props.cellRenderers["ALL"]) {
      return props.cellRenderers["ALL"];
    }
    return undefined;
  };
  headerRenderer = props => {
    return /*#__PURE__*/React.createElement(TableHeader, {
      onDragStart: this.onColumnDragStart,
      onDrop: this.onColumnDrop,
      getHeaderMenuItems: this.getHeaderMenuItems,
      isLastColumn: this.isLastColumn,
      isFirstColumn: this.isFirstColumn,
      rowCount: this.props.rowCount,
      isSortable: this.props.isSortable,
      contextMenu: this.props.contextMenu,
      ...props
    });
  };
  getPixelWidth = () => {
    const width = this.props.width;
    if (_.isString(width)) {
      return this.state.pixelWidth || 100;
    }
    if (!width && this.table && this.table.props.width) {
      return this.table.props.width;
    }
    return width;
  };
  getPixelColumnWidths = () => {
    let totalWidth = 0;
    const componentWidth = this.getPixelWidth();
    const pxWidths = this.state.columnWidths.map((width, index, array) => {
      if (index === array.length - 1) {
        return componentWidth - totalWidth;
      }
      const pxWidth = width > COLUMN_FRACTION_BASE ? COLUMN_FRACTION_BASE : Math.floor(width * componentWidth / COLUMN_FRACTION_BASE);
      totalWidth += pxWidth;
      return pxWidth;
    }, this);
    const numCols = this.props.columns.length;
    if (pxWidths.length < numCols) {
      const width = Math.floor(componentWidth / numCols);
      const remainder = componentWidth % numCols;
      const result = this.props.columns.map((col, index) => {
        if (index === numCols - 1) {
          return width + remainder;
        }
        return width;
      });
      return result;
    }
    return pxWidths;
  };
  _loadTimeout = null;
  loadMoreRows = ({
    startIndex,
    stopIndex
  }) => {
    const timeout = 50;
    clearTimeout(this._loadTimeout);
    this._loadTimeout = setTimeout(() => {
      this.props.loadMoreRows({
        startIndex,
        stopIndex
      });
    }, timeout);
  };
  reloadVisibleRows = (startIndex = this._visibleRowStartIndex) => {
    this.table.forceUpdateGrid();
    this.loadMoreRows({
      startIndex,
      stopIndex: this._visibleRowStopIndex
    });
  };
  render() {
    const tableRowHeight = this.props.useTouch ? ROW_HEIGHT_TOUCH : ROW_HEIGHT;
    const columnWidths = this.getPixelColumnWidths();
    const addClasses = {
      settingsButton: true,
      disabled: this.props.columns.length === 0 || this.props.columns.length === MAX_COLUMNS
    };
    let plusButton = null;
    if (this.props.allColumns !== undefined && this.props.allColumns.length > 0) {
      plusButton = /*#__PURE__*/React.createElement("div", {
        ref: "addButton",
        onClick: this.onPlusButtonClick,
        className: _.classSet(addClasses),
        title: this.props.language.get("nc_table_column_add_column")
      }, "+");
    }
    return /*#__PURE__*/React.createElement("div", {
      ref: "objectTable",
      style: {
        flex: "1 1 auto"
      },
      onMouseDown: this.beginColumnResize
    }, /*#__PURE__*/React.createElement(AutoSizer, {
      ref: "table"
    }, ({
      height,
      width
    }) => /*#__PURE__*/React.createElement(InfiniteLoader, {
      ref: "loader",
      isRowLoaded: this.props.isRowLoaded,
      loadMoreRows: this.loadMoreRows,
      rowCount: this.props.rowCount
    }, ({
      onRowsRendered,
      registerChild
    }) => /*#__PURE__*/React.createElement(InnerTable, {
      ref: table => {
        this.table = table;
        registerChild(table);
      },
      children: this.props.columns.map((column, index) => {
        const style = {};
        if (column.textAlign) {
          style.textAlign = column.textAlign;
        }
        return /*#__PURE__*/React.createElement(Column, {
          label: column.name,
          columnData: {
            style
          },
          headerRenderer: this.headerRenderer,
          cellRenderer: this.getCellRenderer(column, this.props),
          width: columnWidths && columnWidths[index] ? columnWidths[index] : Math.round(1 / this.props.columns.length * 10000),
          style: style,
          dataKey: column.name
        });
      }),
      onRowsRendered: ({
        startIndex,
        stopIndex
      }) => {
        this._visibleRowStartIndex = startIndex;
        this._visibleRowStopIndex = stopIndex;
        onRowsRendered({
          startIndex,
          stopIndex
        });
      },
      headerHeight: 21,
      height: height,
      rowCount: this.props.rowCount,
      rowRenderer: this.rowRenderer,
      rowGetter: this.props.getTableRow,
      rowHeight: tableRowHeight,
      width: width,
      scrollToIndex: this.props.scrollToIndex,
      sort: this.props.onSortingChanged // function, call when header is clicked {sortBy: string, sortDirection: SortDirection}
      ,
      sortBy: `${this.props.sortColumn}` // String, column label, sorted by
      ,
      sortDirection: this.props.sortOrder === 0 ? SortDirection.ASC : SortDirection.DESC // SortDirection.ASC or SortDirection.DESC
    }))), plusButton);
  }
}
Table.HIGHLIGHT = HIGHLIGHT;
export default Table;